import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDate, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Wallet, PlusCircle, MinusCircle, Pencil, TrendingUp, ArrowRight, Lock, Unlock, HandCoins, CheckCircle2, AlertCircle, Trash2, Plus, Repeat, Coins } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

export default function Modal() {
  const [data, setData] = useState(null);
  const [openInit, setOpenInit] = useState(false);
  const [openAdj, setOpenAdj] = useState(false);
  const [form, setForm] = useState({ amount: 0, notes: "" });
  const [adjForm, setAdjForm] = useState({ amount: 0, mode: "add", date: "", notes: "" });
  const [debts, setDebts] = useState([]);
  const [reminders, setReminders] = useState({ upcoming: [], overdue: [], today: "" });
  const [openDebt, setOpenDebt] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);
  const [debtForm, setDebtForm] = useState({
    creditor: "", notes: "",
    installment_amount: 0, monthly_due_day: 15,
    first_due_date: "", end_date: "",
  });
  const [detailDebt, setDetailDebt] = useState(null); // hutang yang sedang dibuka detail schedule-nya

  // Table filter/sort hooks
  const monthlyTF = useTableFilter(data?.monthly_breakdown || [], {
    searchKeys: ["month", "label"], defaultSort: { key: "month", dir: "desc" },
  });
  const debtTF = useTableFilter(debts, {
    searchKeys: ["creditor", "notes"], defaultSort: { key: "next_due_date", dir: "asc" },
  });
  const adjTF = useTableFilter(data?.adjustments || [], {
    searchKeys: ["notes"], defaultSort: { key: "date", dir: "desc" },
  });

  // Preview cicilan (real-time saat user isi form) — Mode B
  const previewSchedule = () => {
    const amt = Number(debtForm.installment_amount) || 0;
    const dueDay = Math.min(Math.max(Number(debtForm.monthly_due_day) || 1, 1), 28);
    if (!debtForm.first_due_date || !debtForm.end_date || amt <= 0) return null;
    const first = new Date(debtForm.first_due_date + "T00:00:00");
    const end = new Date(debtForm.end_date + "T00:00:00");
    if (end < first) return null;
    const arr = [];
    let y = first.getFullYear(), m = first.getMonth();
    while (true) {
      const d = new Date(y, m, dueDay);
      if (d > end) break;
      arr.push(d.toISOString().slice(0, 10));
      m += 1;
      if (m > 11) { m = 0; y += 1; }
      if (arr.length > 240) break;
    }
    return { count: arr.length, total: arr.length * amt, dates: arr };
  };

  const payInstallment = async (debt, installmentNumber) => {
    try {
      await api.post(`/debts/${debt.id}/pay`, { installment_number: installmentNumber });
      toast.success(`Cicilan #${installmentNumber} dilunasi (tidak memotong kas)`);
      await loadDebts();
      // Refresh detail juga
      const fresh = (await api.get("/debts")).data.find((d) => d.id === debt.id);
      if (fresh && detailDebt?.id === debt.id) setDetailDebt(fresh);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const unpayInstallment = async (debt, installmentNumber) => {
    if (!window.confirm(`Batalkan pembayaran cicilan #${installmentNumber}?`)) return;
    try {
      await api.post(`/debts/${debt.id}/unpay`, { installment_number: installmentNumber });
      toast.success(`Cicilan #${installmentNumber} dikembalikan ke status belum lunas`);
      await loadDebts();
      const fresh = (await api.get("/debts")).data.find((d) => d.id === debt.id);
      if (fresh && detailDebt?.id === debt.id) setDetailDebt(fresh);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loadDebts = async () => {
    const [rDebts, rRem] = await Promise.all([api.get("/debts"), api.get("/debts/reminders")]);
    setDebts(rDebts.data);
    setReminders(rRem.data);
  };
  const load = () => api.get("/modal").then((r) => setData(r.data));
  useEffect(() => { load(); loadDebts(); }, []);

  const saveDebt = async () => {
    if (!debtForm.creditor.trim()) return toast.error("Nama kreditor wajib");
    if (editingDebt) {
      // Edit hanya boleh ubah creditor / notes
      try {
        await api.put(`/debts/${editingDebt.id}`, {
          creditor: debtForm.creditor, notes: debtForm.notes,
          installment_amount: 1, monthly_due_day: 1, end_date: "2099-12-31",
        });
        toast.success("Info hutang diperbarui");
        setOpenDebt(false); setEditingDebt(null); loadDebts();
      } catch (e) { toast.error(errMsg(e)); }
      return;
    }
    if (Number(debtForm.installment_amount) <= 0) return toast.error("Nominal per cicilan wajib > 0");
    if (!debtForm.first_due_date || !debtForm.end_date) return toast.error("Tanggal cicilan pertama & jatuh tempo akhir wajib diisi");
    if (debtForm.end_date < debtForm.first_due_date) return toast.error("Tanggal jatuh tempo akhir harus setelah cicilan pertama");
    try {
      await api.post("/debts", {
        creditor: debtForm.creditor.trim(),
        notes: debtForm.notes || "",
        installment_amount: Number(debtForm.installment_amount),
        monthly_due_day: Number(debtForm.monthly_due_day),
        first_due_date: debtForm.first_due_date,
        end_date: debtForm.end_date,
      });
      toast.success("Hutang & jadwal cicilan tersimpan");
      setOpenDebt(false); loadDebts();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const delDebt = async (id) => {
    if (!window.confirm("Hapus hutang ini beserta seluruh jadwal cicilan?")) return;
    try { await api.delete(`/debts/${id}`); toast.success("Dihapus"); loadDebts(); if (detailDebt?.id === id) setDetailDebt(null); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openNewDebt = () => {
    setEditingDebt(null);
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 15);
    const inOneYear = new Date(today.getFullYear() + 1, today.getMonth(), 15);
    setDebtForm({
      creditor: "", notes: "",
      installment_amount: 0, monthly_due_day: 15,
      first_due_date: nextMonth.toISOString().slice(0, 10),
      end_date: inOneYear.toISOString().slice(0, 10),
    });
    setOpenDebt(true);
  };
  const openEditDebt = (d) => {
    setEditingDebt(d);
    setDebtForm({
      creditor: d.creditor, notes: d.notes || "",
      installment_amount: d.installment_amount || 0,
      monthly_due_day: d.monthly_due_day || 15,
      first_due_date: d.first_due_date || "",
      end_date: d.end_date || "",
    });
    setOpenDebt(true);
  };

  const lockMonth = async (year, month, key) => {
    if (!window.confirm(`Kunci laba bulan ${key}? Setelah dikunci, angka tidak berubah walau ada revisi transaksi.`)) return;
    try {
      await api.post(`/modal/lock-month/${year}/${month}`);
      toast.success(`Laba bulan ${key} dikunci`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const unlockMonth = async (key) => {
    if (!window.confirm(`Buka kunci bulan ${key}?`)) return;
    try {
      await api.delete(`/modal/lock-month/${key}`);
      toast.success("Kunci dibuka");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveInitial = async () => {
    try {
      await api.post("/modal", { amount: Number(form.amount), notes: form.notes });
      toast.success("Modal awal tersimpan");
      setOpenInit(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveAdjustment = async () => {
    const amt = Number(adjForm.amount) * (adjForm.mode === "withdraw" ? -1 : 1);
    if (!amt) return toast.error("Nominal tidak valid");
    try {
      await api.post("/modal/adjust", { amount: amt, date: adjForm.date || undefined, notes: adjForm.notes });
      toast.success(adjForm.mode === "add" ? "Modal ditambahkan" : "Modal ditarik");
      setOpenAdj(false); setAdjForm({ amount: 0, mode: "add", date: "", notes: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const delAdj = async (id) => {
    if (!window.confirm("Hapus penyesuaian ini?")) return;
    try { await api.delete(`/modal/adjust/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="p-8 text-slate-500">Memuat...</div>;

  const timelineChart = data.events.map((e) => ({
    date: e.date ? e.date.slice(0, 10) : "-",
    label: e.label,
    balance: e.running,
  }));

  return (
    <>
      <PageHeader
        title="Modal & Kewajiban"
        subtitle="Modal usaha owner + rekap hutang & tempo pembayaran"
        testId="page-modal"
      />

      <div className="flex justify-end gap-2 mb-4">
            <Button variant="outline" onClick={()=>{setAdjForm({amount:0,mode:"add",date:new Date().toISOString().slice(0,10),notes:""});setOpenAdj(true);}} data-testid="btn-adjust-modal">
              <PlusCircle className="w-4 h-4 mr-2"/> Tambah / Tarik Modal
            </Button>
            <Button onClick={()=>{setForm({amount:data.modal_awal||0,notes:data.modal_notes||""});setOpenInit(true);}} data-testid="btn-set-modal" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
              <Pencil className="w-4 h-4 mr-2"/> {data.modal_awal>0 ? "Ubah Modal Awal" : "Atur Modal Awal"}
            </Button>
          </div>

      {data.modal_awal === 0 && (
        <Card className="p-8 border border-dashed border-emerald-300 bg-emerald-50 mb-6 text-center">
          <Wallet className="w-10 h-10 mx-auto text-[var(--hotto-primary)] mb-3"/>
          <h3 className="text-lg font-semibold mb-2" style={{fontFamily:'Outfit'}}>Belum ada Modal Awal</h3>
          <p className="text-sm text-slate-600 mb-4 max-w-md mx-auto">
            Modal awal adalah jumlah uang yang Anda investasikan ke bisnis saat ini. Setelahnya, sistem otomatis menambah dari laba bersih tiap bulan.
          </p>
          <Button onClick={()=>{setForm({amount:0,notes:""});setOpenInit(true);}} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
            Atur Modal Awal Sekarang
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
          <div className="overline mb-2">Modal Awal</div>
          <div className="font-mono-num text-xl font-bold">{formatIDR(data.modal_awal)}</div>
          {data.modal_date && <div className="text-xs text-slate-500 mt-1">{formatDate(data.modal_date)}</div>}
        </Card>
        <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
          <div className="overline mb-2 flex items-center gap-1"><PlusCircle className="w-3 h-3 text-emerald-600"/>Laba Bersih Terkumpul</div>
          <div className="font-mono-num text-xl font-bold text-[var(--hotto-success)]">+{formatIDR(data.total_profit_realized)}</div>
          <div className="text-xs text-slate-500 mt-1">{data.monthly_breakdown.length} bulan</div>
        </Card>
        <Card className="p-5 border-2 border-[var(--hotto-primary)] rounded-md stat-card">
          <div className="overline mb-2">Modal Sekarang</div>
          <div className={`font-mono-num text-2xl font-bold ${data.current_modal>=0?"text-[var(--hotto-primary)]":"text-[var(--hotto-destructive)]"}`}>{formatIDR(data.current_modal)}</div>
          {data.total_adjustments !== 0 && <div className="text-xs text-slate-500 mt-1">Termasuk penyesuaian {formatIDR(data.total_adjustments)}</div>}
        </Card>
      </div>

      {/* Formula visual */}
      <Card className="p-4 border border-[var(--hotto-border)] rounded-md mb-6 bg-emerald-50/40">
        <div className="text-xs text-slate-500 mb-2 text-center">Rumus: <b>Modal Awal + Penyesuaian + Laba Bersih = Modal Sekarang</b></div>
        <div className="flex items-center flex-wrap gap-2 justify-center text-sm">
          <span className="font-mono-num font-semibold">{formatIDR(data.modal_awal)}</span>
          <span className="text-slate-400">(awal)</span>
          {data.total_adjustments !== 0 && (<>
            <span className={data.total_adjustments>0?"text-emerald-600":"text-red-600"}>{data.total_adjustments>0?"+":""}</span>
            <span className="font-mono-num">{formatIDR(Math.abs(data.total_adjustments))}</span>
            <span className="text-slate-400">(setor/tarik)</span>
          </>)}
          <span className="text-emerald-600">+</span>
          <span className="font-mono-num">{formatIDR(data.total_profit_realized)}</span>
          <span className="text-slate-400">(laba bersih)</span>
          <ArrowRight className="w-4 h-4 text-slate-400"/>
          <span className={`font-mono-num font-bold text-base ${data.current_modal>=0?"text-[var(--hotto-primary)]":"text-red-600"}`}>{formatIDR(data.current_modal)}</span>
        </div>
      </Card>

      {/* Timeline chart */}
      {timelineChart.length > 0 && (
        <Card className="p-6 border border-[var(--hotto-border)] rounded-md mb-6">
          <div className="overline mb-3">Riwayat Modal (Running Balance dari Event)</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timelineChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                <XAxis dataKey="label" stroke="#64748b" fontSize={10} angle={-25} textAnchor="end" height={60}/>
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v)=>`${(v/1000000).toFixed(1)}jt`}/>
                <Tooltip formatter={(v)=>formatIDR(v)}/>
                <ReferenceLine y={0} stroke="#94a3b8"/>
                <Line type="monotone" dataKey="balance" stroke="#047857" strokeWidth={2.5} dot={{r:4, fill:"#047857"}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Monthly profit breakdown */}
      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden mb-6">
        <div className="p-4 border-b border-[var(--hotto-border)] flex items-center gap-2 bg-slate-50">
          <TrendingUp className="w-4 h-4 text-[var(--hotto-primary)]"/>
          <div className="overline">Laba Bulanan (yang menambah modal)</div>
        </div>
        <div className="p-3 border-b border-[var(--hotto-border)] bg-white">
          <TableToolbar tf={monthlyTF} placeholder="Cari bulan..." testId="modal-monthly-search"/>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <SortHeader tf={monthlyTF} col="month">Bulan</SortHeader>
              <SortHeader tf={monthlyTF} col="revenue" className="text-right">Pendapatan</SortHeader>
              <SortHeader tf={monthlyTF} col="cogs" className="text-right">HPP</SortHeader>
              <SortHeader tf={monthlyTF} col="expense" className="text-right">Beban</SortHeader>
              <SortHeader tf={monthlyTF} col="net_profit" className="text-right">Laba Bersih</SortHeader>
              <TableHead className="text-right w-28">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {monthlyTF.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada laba bulanan sejak modal awal ditetapkan.</TableCell></TableRow>}
            {monthlyTF.rows.map((m) => (
              <TableRow key={m.month} className="table-row-hover">
                <TableCell className="font-medium" style={{fontFamily:'Outfit'}}>
                  <div className="flex items-center gap-2">
                    {m.month}
                    {m.locked && <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px] h-4"><Lock className="w-2.5 h-2.5 mr-1"/>Terkunci</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono-num text-[var(--hotto-success)]">{formatIDR(m.revenue)}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-500">{formatIDR(m.cogs)}</TableCell>
                <TableCell className="text-right font-mono-num text-[var(--hotto-destructive)]">{formatIDR(m.expense)}</TableCell>
                <TableCell className="text-right font-mono-num font-bold">
                  <Badge className={m.net_profit>=0?"bg-emerald-50 text-emerald-700 hover:bg-emerald-50":"bg-red-50 text-red-700 hover:bg-red-50"}>
                    {formatIDR(m.net_profit)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {m.locked ? (
                    <Button size="sm" variant="ghost" onClick={()=>unlockMonth(m.month)} data-testid={`btn-unlock-${m.month}`}>
                      <Unlock className="w-3 h-3 mr-1"/> Buka
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={()=>lockMonth(m.year, m.month_num, m.month)} data-testid={`btn-lock-${m.month}`}>
                      <Lock className="w-3 h-3 mr-1"/> Kunci
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Adjustments */}
      {data.adjustments.length > 0 && (
        <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
          <div className="p-4 border-b border-[var(--hotto-border)] bg-slate-50 overline">Penyesuaian Modal Manual</div>
          <div className="p-3 border-b border-[var(--hotto-border)] bg-white">
            <TableToolbar tf={adjTF} placeholder="Cari catatan..." testId="modal-adj-search"/>
          </div>
          <Table>
            <TableHeader><TableRow>
              <SortHeader tf={adjTF} col="date">Tanggal</SortHeader>
              <SortHeader tf={adjTF} col="notes">Catatan</SortHeader>
              <SortHeader tf={adjTF} col="amount" className="text-right">Nominal</SortHeader>
              <TableHead className="w-16"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {adjTF.rows.map((a) => (
                <TableRow key={a.id} className="table-row-hover">
                  <TableCell className="font-mono-num text-sm">{formatDate(a.date)}</TableCell>
                  <TableCell>{a.notes || "-"}</TableCell>
                  <TableCell className={`text-right font-mono-num font-semibold ${a.amount>=0?"text-emerald-600":"text-red-600"}`}>{a.amount>=0?"+":""}{formatIDR(a.amount)}</TableCell>
                  <TableCell className="text-right"><Button size="icon" variant="ghost" onClick={()=>delAdj(a.id)}>×</Button></TableCell>
                </TableRow>
              ))}
        </TableBody>
          </Table>
        </Card>
      )}

      {/* ===== Hutang & Kewajiban (inline section) ===== */}
      <div className="mt-10 pt-6 border-t-2 border-dashed border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Hutang & Kewajiban</div>
            <h3 className="text-xl font-bold mt-1" style={{fontFamily:'Outfit'}}>Rekap Hutang</h3>
            <p className="text-sm text-slate-500 mt-1"><b>Hutang berdiri sendiri</b> — tidak memotong Arus Kas atau Modal. Input dengan cicilan & jatuh tempo, lalu klik <b>Bayar</b> untuk mencatat pelunasan cicilan.</p>
          </div>
          <Button onClick={openNewDebt} data-testid="btn-add-debt" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
            <Plus className="w-4 h-4 mr-2"/> Tambah Hutang
          </Button>
        </div>

          {(() => {
            const unpaid = debts.filter(d=>!d.paid);
            const totalUnpaid = unpaid.reduce((s,d)=>s+Number(d.remaining||0),0);
            const totalOriginal = debts.reduce((s,d)=>s+Number(d.total_amount||0),0);
            const totalPaid = debts.reduce((s,d)=>s+Number(d.paid_amount||0),0);
            const nextDue = unpaid.filter(d=>d.next_due_date)
              .sort((a,b)=>(a.next_due_date||"").localeCompare(b.next_due_date||""))[0];
            const totalOverdueCicilan = reminders.overdue?.length || 0;
            const totalOverdueAmt = (reminders.overdue||[]).reduce((s,x)=>s+Number(x.amount||0),0);
            return (
              <>
                {(reminders.upcoming?.length > 0 || reminders.overdue?.length > 0) && (
                  <Card className="p-4 border-l-4 border-l-red-500 bg-red-50/50 mb-4" data-testid="debt-reminder-banner">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5"/>
                      <div className="flex-1">
                        <div className="font-bold text-red-800 mb-1">Pengingat Cicilan</div>
                        {reminders.overdue?.length > 0 && (
                          <div className="text-xs text-red-700 mb-2">
                            <b>{reminders.overdue.length} cicilan TERLAMBAT</b> · Total {formatIDR(reminders.overdue.reduce((s,x)=>s+Number(x.amount||0),0))}
                            <ul className="mt-1 ml-4 list-disc space-y-0.5">
                              {reminders.overdue.slice(0, 3).map((r) => (
                                <li key={`${r.debt_id}-${r.installment_number}`}>{r.creditor} — cicilan #{r.installment_number} · {formatDate(r.due_date)} · <b>telat {r.days_late} hari</b></li>
                              ))}
                              {reminders.overdue.length > 3 && <li className="text-slate-500 list-none">+ {reminders.overdue.length - 3} lainnya</li>}
                            </ul>
                          </div>
                        )}
                        {reminders.upcoming?.length > 0 && (
                          <div className="text-xs text-amber-800">
                            <b>{reminders.upcoming.length} cicilan jatuh tempo dalam 3 hari</b>
                            <ul className="mt-1 ml-4 list-disc space-y-0.5">
                              {reminders.upcoming.map((r) => (
                                <li key={`${r.debt_id}-${r.installment_number}`}>
                                  {r.creditor} — cicilan #{r.installment_number} · {formatDate(r.due_date)} · <b>{r.days_left === 0 ? "HARI INI" : r.days_left === 1 ? "BESOK (H-1)" : `${r.days_left} hari lagi`}</b> · {formatIDR(r.amount)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                )}
                <div className="grid grid-cols-4 gap-4 mb-4">
                <Card className="p-5 border-2 border-red-300 bg-red-50/40 rounded-md stat-card">
                  <div className="overline mb-2 flex items-center gap-1"><HandCoins className="w-3 h-3 text-red-600"/> Sisa Hutang</div>
                  <div className="font-mono-num text-2xl font-bold text-red-600">{formatIDR(totalUnpaid)}</div>
                  <div className="text-xs text-slate-500 mt-1">{unpaid.length} hutang aktif dari {formatIDR(totalOriginal)}</div>
                </Card>
                <Card className="p-5 border-2 border-orange-300 bg-orange-50/40 rounded-md stat-card">
                  <div className="overline mb-2 flex items-center gap-1"><AlertCircle className="w-3 h-3 text-orange-600"/> Tempo Berikutnya</div>
                  <div className="font-mono-num text-lg font-bold text-orange-600">{nextDue ? formatDate(nextDue.next_due_date) : "-"}</div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{nextDue ? `${nextDue.creditor} · cicilan #${nextDue.next_installment_number}` : "Tidak ada tempo mendatang"}</div>
                </Card>
                <Card className="p-5 border-2 border-red-400 bg-red-50 rounded-md stat-card">
                  <div className="overline mb-2 flex items-center gap-1"><AlertCircle className="w-3 h-3 text-red-700"/> Cicilan Terlambat</div>
                  <div className="font-mono-num text-2xl font-bold text-red-700">{totalOverdueCicilan}</div>
                  <div className="text-xs text-slate-500 mt-1">{formatIDR(totalOverdueAmt)}</div>
                </Card>
                <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
                  <div className="overline mb-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600"/> Total Terbayar</div>
                  <div className="font-mono-num text-2xl font-bold text-emerald-600">{formatIDR(totalPaid)}</div>
                  <div className="text-xs text-slate-500 mt-1">{debts.filter(d=>d.paid).length} lunas</div>
                </Card>
                </div>
              </>
            );
          })()}

          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b border-[var(--hotto-border)]">
              <TableToolbar tf={debtTF} placeholder="Cari kreditor / catatan..." testId="debt-search"/>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Status</TableHead>
                  <SortHeader tf={debtTF} col="creditor">Kepada (Kreditor)</SortHeader>
                  <SortHeader tf={debtTF} col="next_due_date">Tempo Berikutnya</SortHeader>
                  <SortHeader tf={debtTF} col="installment_amount" className="text-right">Cicilan/bln</SortHeader>
                  <SortHeader tf={debtTF} col="total_amount" className="text-right">Total</SortHeader>
                  <SortHeader tf={debtTF} col="paid_amount" className="text-right">Terbayar</SortHeader>
                  <TableHead className="text-right">Sisa</TableHead>
                  <TableHead className="text-center">Sisa Cicilan</TableHead>
                  <TableHead className="w-56 text-center">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {debtTF.rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-500">Belum ada hutang tercatat.</TableCell></TableRow>}
                {debtTF.rows.map((d, idx) => {
                  const today = new Date().toISOString().slice(0,10);
                  const isOverdue = !d.paid && d.overdue_count > 0;
                  const remaining = Number(d.remaining || 0);
                  const remainingInst = Number(d.remaining_installments || 0);
                  const nextDue = d.next_due_date;
                  const daysLeft = nextDue ? Math.round((new Date(nextDue) - new Date(today)) / 86400000) : null;
                  const nearDue = !d.paid && daysLeft !== null && daysLeft >= 0 && daysLeft <= 3;
                  return (
                    <React.Fragment key={d.id}>
                    <TableRow
                      className={`table-row-hover cursor-pointer ${isOverdue?"bg-red-50/60":nearDue?"bg-amber-50/60":d.paid?"opacity-60":""}`}
                      onClick={() => setDetailDebt(detailDebt?.id === d.id ? null : d)}
                      data-testid={`debt-row-${d.id}`}
                    >
                      <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                      <TableCell>
                        {d.paid ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1"/>Lunas</Badge>
                        ) : isOverdue ? (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><AlertCircle className="w-3 h-3 mr-1"/>Terlambat ({d.overdue_count})</Badge>
                        ) : nearDue ? (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><AlertCircle className="w-3 h-3 mr-1"/>H-{daysLeft}</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Repeat className="w-3 h-3 mr-1"/>{d.paid_installments}/{d.installment_count}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{d.creditor}</div>
                        {d.notes && <div className="text-xs text-slate-500 max-w-xs truncate">{d.notes}</div>}
                      </TableCell>
                      <TableCell className="font-mono-num text-sm">
                        {nextDue ? (
                          <div>
                            <div className={isOverdue?"text-red-700 font-semibold":nearDue?"text-amber-700 font-semibold":""}>{formatDate(nextDue)}</div>
                            <div className="text-[10px] text-slate-500">
                              {daysLeft < 0 ? `Telat ${Math.abs(daysLeft)} hari` : daysLeft === 0 ? "Hari ini" : `${daysLeft} hari lagi`}
                            </div>
                          </div>
                        ) : <span className="text-slate-400">Lunas</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-sm">{formatIDR(d.installment_amount || 0)}</TableCell>
                      <TableCell className="text-right font-mono-num font-bold">{formatIDR(d.total_amount || 0)}</TableCell>
                      <TableCell className="text-right font-mono-num text-emerald-700">{formatIDR(d.paid_amount || 0)}</TableCell>
                      <TableCell className={`text-right font-mono-num font-semibold ${remaining>0?"text-red-600":"text-slate-400"}`}>{formatIDR(remaining)}</TableCell>
                      <TableCell className="text-center text-sm">
                        <span className={`font-mono-num font-semibold ${remainingInst===0?"text-emerald-600":"text-blue-700"}`}>
                          {remainingInst}× lagi
                        </span>
                      </TableCell>
                      <TableCell className="text-right" onClick={(e)=>e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          {!d.paid && d.next_installment_number && (
                            <Button size="sm" onClick={()=>payInstallment(d, d.next_installment_number)} data-testid={`btn-pay-${d.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 text-xs">
                              <Coins className="w-3 h-3 mr-1"/> Bayar #{d.next_installment_number}
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={()=>openEditDebt(d)} title="Edit info"><Pencil className="w-3.5 h-3.5"/></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={()=>delDebt(d.id)} title="Hapus"><Trash2 className="w-3.5 h-3.5 text-red-500"/></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {detailDebt?.id === d.id && (
                      <TableRow className="bg-slate-50/60">
                        <TableCell colSpan={10} className="p-4">
                          <div className="mb-2 flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-700">Jadwal Cicilan — {d.creditor}</div>
                              <div className="text-xs text-slate-500">Klik cicilan mana saja untuk membayar (boleh lebih awal / lompat urutan)</div>
                            </div>
                            <Button size="sm" variant="ghost" onClick={()=>setDetailDebt(null)}>Tutup</Button>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid={`schedule-${d.id}`}>
                            {(d.schedule || []).map((s) => {
                              const dl = Math.round((new Date(s.due_date) - new Date(today)) / 86400000);
                              const late = !s.paid && dl < 0;
                              const near = !s.paid && dl >= 0 && dl <= 3;
                              return (
                                <div
                                  key={s.installment_number}
                                  className={`p-2 rounded-md border text-xs ${
                                    s.paid ? "bg-emerald-50 border-emerald-300"
                                    : late ? "bg-red-50 border-red-300"
                                    : near ? "bg-amber-50 border-amber-300"
                                    : "bg-white border-slate-200"
                                  }`}
                                >
                                  <div className="flex items-start justify-between mb-1">
                                    <span className="font-bold">#{s.installment_number}</span>
                                    {s.paid && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/>}
                                    {late && <AlertCircle className="w-3.5 h-3.5 text-red-600"/>}
                                    {near && <AlertCircle className="w-3.5 h-3.5 text-amber-600"/>}
                                  </div>
                                  <div className="font-mono-num text-[11px]">{formatDate(s.due_date)}</div>
                                  <div className="font-mono-num text-[11px] text-slate-600">{formatIDR(s.amount)}</div>
                                  {s.paid ? (
                                    <div className="mt-1">
                                      <div className="text-[10px] text-emerald-700">Dibayar {formatDate(s.paid_at)}</div>
                                      <Button size="sm" variant="ghost" onClick={()=>unpayInstallment(d, s.installment_number)} className="h-6 text-[10px] px-1 mt-1 w-full text-red-500 hover:text-red-600">Batalkan</Button>
                                    </div>
                                  ) : (
                                    <Button size="sm" onClick={()=>payInstallment(d, s.installment_number)} className="w-full h-6 text-[10px] mt-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`btn-pay-installment-${d.id}-${s.installment_number}`}>
                                      Bayar
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}
        </TableBody>
            </Table>
          </Card>
      </div>

      {/* Dialog: Set Modal Awal */}
      <Dialog open={openInit} onOpenChange={setOpenInit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Atur Modal Awal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
              <b>Catatan:</b> Modal awal adalah kondisi kas + inventaris awal ketika mulai menggunakan sistem. Perhitungan akan menghitung laba <b>sejak tanggal ini</b>.
            </div>
            <div><Label>Nominal Modal Awal (Rp)</Label><Input type="number" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} data-testid="input-modal-amount"/></div>
            <div><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} placeholder="mis. Investasi awal owner, per Jan 2026"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenInit(false)}>Batal</Button>
            <Button onClick={saveInitial} data-testid="btn-save-modal" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Adjust Modal */}
      <Dialog open={openAdj} onOpenChange={setOpenAdj}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah / Tarik Modal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Jenis</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={()=>setAdjForm({...adjForm,mode:"add"})}
                  className={`p-3 rounded-md border text-sm flex items-center gap-2 ${adjForm.mode==="add"?"border-emerald-500 bg-emerald-50 text-emerald-700 font-medium":"border-slate-200 text-slate-600"}`}
                  data-testid="btn-mode-add"
                ><PlusCircle className="w-4 h-4"/> Setor Modal</button>
                <button
                  type="button"
                  onClick={()=>setAdjForm({...adjForm,mode:"withdraw"})}
                  className={`p-3 rounded-md border text-sm flex items-center gap-2 ${adjForm.mode==="withdraw"?"border-red-500 bg-red-50 text-red-700 font-medium":"border-slate-200 text-slate-600"}`}
                  data-testid="btn-mode-withdraw"
                ><MinusCircle className="w-4 h-4"/> Tarik Modal</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nominal (Rp)</Label><Input type="number" value={adjForm.amount} onChange={(e)=>setAdjForm({...adjForm,amount:e.target.value})} data-testid="input-adj-amount"/></div>
              <div><Label>Tanggal</Label><Input type="date" value={adjForm.date} onChange={(e)=>setAdjForm({...adjForm,date:e.target.value})} data-testid="input-adj-date"/></div>
            </div>
            <div><Label>Catatan</Label><Textarea rows={2} value={adjForm.notes} onChange={(e)=>setAdjForm({...adjForm,notes:e.target.value})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenAdj(false)}>Batal</Button>
            <Button onClick={saveAdjustment} data-testid="btn-save-adjust" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Hutang (Mode B — cicilan + jatuh tempo akhir) */}
      <Dialog open={openDebt} onOpenChange={setOpenDebt}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingDebt ? "Edit Info Hutang" : "Tambah Hutang Baru"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Kepada (Badan / Perseorangan)</Label>
              <Input value={debtForm.creditor} onChange={(e)=>setDebtForm({...debtForm,creditor:e.target.value})}
                placeholder="mis. Bank Mandiri KTA / Pak Budi" data-testid="input-debt-creditor"/>
            </div>
            <div><Label>Catatan (opsional)</Label>
              <Textarea rows={2} value={debtForm.notes} onChange={(e)=>setDebtForm({...debtForm,notes:e.target.value})}
                placeholder="mis. KTA modal kerja tokobinaan, cicilan mobil operasional"/>
            </div>

            {!editingDebt && (
              <>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-800">
                  <b>Skema Cicilan (Mode B)</b> — sistem akan otomatis membuat jadwal cicilan bulanan dari tanggal cicilan pertama sampai jatuh tempo akhir, pada hari tempo tiap bulan yang Anda pilih.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nominal per Cicilan (Rp)</Label>
                    <Input type="number" value={debtForm.installment_amount}
                      onChange={(e)=>setDebtForm({...debtForm,installment_amount:e.target.value})}
                      placeholder="mis. 1000000" data-testid="input-installment-amount"/>
                  </div>
                  <div><Label>Tempo tiap Tanggal (1-28)</Label>
                    <Input type="number" min={1} max={28} value={debtForm.monthly_due_day}
                      onChange={(e)=>setDebtForm({...debtForm,monthly_due_day:e.target.value})}
                      placeholder="mis. 15" data-testid="input-monthly-due-day"/>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Cicilan Pertama Tanggal</Label>
                    <Input type="date" value={debtForm.first_due_date}
                      onChange={(e)=>setDebtForm({...debtForm,first_due_date:e.target.value})}
                      data-testid="input-first-due-date"/>
                  </div>
                  <div><Label>Jatuh Tempo Akhir</Label>
                    <Input type="date" value={debtForm.end_date}
                      onChange={(e)=>setDebtForm({...debtForm,end_date:e.target.value})}
                      data-testid="input-end-date"/>
                  </div>
                </div>

                {/* Preview jadwal cicilan */}
                {(() => {
                  const p = previewSchedule();
                  if (!p) return <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-500">Isi semua field di atas untuk melihat preview jadwal.</div>;
                  return (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="preview-schedule">
                      <div className="text-xs text-emerald-800 mb-2">
                        <b>Preview:</b> {p.count} cicilan × {formatIDR(Number(debtForm.installment_amount))} = <b>{formatIDR(p.total)}</b>
                      </div>
                      <div className="text-[10px] text-emerald-700 max-h-20 overflow-y-auto">
                        {p.dates.slice(0, 12).join(" · ")}{p.dates.length > 12 ? ` ... (+${p.dates.length - 12} lagi)` : ""}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {editingDebt && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                Anda hanya bisa mengedit <b>nama kreditor</b> dan <b>catatan</b>. Untuk mengubah nominal / jadwal, hapus hutang ini lalu buat baru.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenDebt(false)}>Batal</Button>
            <Button onClick={saveDebt} data-testid="btn-save-debt" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
              {editingDebt ? "Simpan Perubahan" : "Buat Hutang & Jadwal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
